import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useBFContext } from '@context';
import { Button, CustomLenses } from '@components';
import { BASE_FRAME_LENS_OPTIONS, RX_TYPE } from '@utils/constants/base-skus';
import { BUILD_FLOW_STEPS, LOCALE_CODES } from '@constants';
import { useFeatureInLocale, useTranslation } from '@utils/index';
import Prescriptions from '../Prescriptions';
import LensPackages from '../LensPackages';
import styles from './QuickAdd.module.scss';
import QuickAddAccordion, { QuickAddAccordionProps } from './QuickAddAccordion';

const getTranslatedTexts = translator => {
	return {
		varifocal: translator('varifocal'),
		blPlusLr: translator('bl+lr'),
		bl: translator('bl'),
		selectYourLenses: translator('select-your-lenses'),
		required: translator('required'),
		chooseYourLensType: translator('choose-your-lens-type'),
		customizeYourLenses: translator('customize-your-lenses'),
		selectAny: translator('select-any'),
		noUpgrades: translator('no-upgrades'),
		noUpgradesSelected: translator('no-upgrades-selected'),
		premiumProgressive: translator('premium-progressive'),
		signatureProgressive: translator('signature-progressive'),
	};
};

const QuickAdd = () => {
	const isLowerTierProgressive = useFeatureInLocale('is-lower-tier-progressive', LOCALE_CODES.US);
	const isNoUpgrades = useFeatureIsOn('is-no-upgrades');
	const {
		rxType,
		lensPackage,
		selectedLenses,
		setStep,
		readerRx,
		isQuickAdd,
		materialOptions,
		setNoUpgradesConfirmation,
		noUpgradesConfirmation,
		hasUpgrades,
	} = useBFContext();
	const [isLensOpen, setIsLensOpen] = useState(true);
	const [isPackageOpen, setIsPackageOpen] = useState(false);
	const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
	const customizeStepRef = useRef<HTMLDivElement>(null);
	const { locale } = useRouter();
	const [dataTag, setDataTag] = useState(isQuickAdd && { 'data-open-buildflow': true });
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);
	let localizedRxType = locale === LOCALE_CODES.GB && rxType === RX_TYPE.PROGRESSIVE ? translations.varifocal : rxType;
	if (isLowerTierProgressive) {
		if (rxType === RX_TYPE.PROGRESSIVE) localizedRxType = translations.signatureProgressive;
		if (rxType === RX_TYPE.PREMIUM_PROGRESSIVE) localizedRxType = translations.premiumProgressive;
	}
	const materialCopy = materialOptions.find(mat => mat.type === lensPackage);
	const isBfAltCopy = useFeatureIsOn('is-bf-alt-copy');

	const handleLensOnClick = () => {
		setDataTag(null);
		setIsLensOpen(!isLensOpen);
		if (isPackageOpen) setIsPackageOpen(false);
		if (isCustomizeOpen) setIsCustomizeOpen(false);
		if (isLowerTierProgressive && (rxType === RX_TYPE.PROGRESSIVE || rxType === RX_TYPE.PREMIUM_PROGRESSIVE))
			setStep(BUILD_FLOW_STEPS.PROGRESSIVE);
		else setStep(BUILD_FLOW_STEPS.LENS);
	};

	const handlePackageOnClick = () => {
		setDataTag(null);
		setIsPackageOpen(!isPackageOpen);
		if (isLensOpen) setIsLensOpen(false);
		if (isCustomizeOpen) setIsCustomizeOpen(false);
	};

	const handleCustomizeOnClick = () => {
		setDataTag(null);
		setIsCustomizeOpen(!isCustomizeOpen);
		if (isLensOpen) setIsLensOpen(false);
		if (isPackageOpen) setIsPackageOpen(false);
		setStep(BUILD_FLOW_STEPS.CUSTOMIZE);
	};

	useEffect(() => {
		if (!!rxType && (rxType !== RX_TYPE.READERS || (rxType === RX_TYPE.READERS && !!readerRx))) {
			handlePackageOnClick();
		}
		if (!!lensPackage) {
			handleCustomizeOnClick();
		}
	}, [lensPackage, rxType, readerRx]);

	const parsedSelectedLenses = selectedLenses.includes(BASE_FRAME_LENS_OPTIONS.LIGHT_RESPONSIVE)
		? translations.blPlusLr
		: selectedLenses.includes(BASE_FRAME_LENS_OPTIONS.BLUE_LIGHT)
			? translations.bl
			: null;

	const data: QuickAddAccordionProps[] = [
		/* Rx */
		{
			isOpen: isLensOpen,
			onClick: handleLensOnClick,
			title: translations.selectYourLenses,
			subtitle: !!rxType ? localizedRxType : translations.required,
			children: <Prescriptions />,
			type: BUILD_FLOW_STEPS.LENS,
		},
		/* Lens Package */
		{
			isOpen: isPackageOpen,
			onClick: handlePackageOnClick,
			title: translations.chooseYourLensType,
			subtitle: !!lensPackage ? (!isBfAltCopy ? lensPackage : materialCopy.altTitle) : translations.required,
			children: <LensPackages />,
			disableModal: true,
			type: BUILD_FLOW_STEPS.PACKAGE,
			disabled: isLensOpen && isNoUpgrades,
		},
		/* Lens Add Ons */
		{
			isOpen: isCustomizeOpen,
			onClick: handleCustomizeOnClick,
			title: translations.customizeYourLenses,
			subtitle: noUpgradesConfirmation
				? translations.noUpgradesSelected
				: !!parsedSelectedLenses
					? parsedSelectedLenses
					: translations.selectAny,
			children: (
				<>
					<CustomLenses ref={customizeStepRef} />
					{isNoUpgrades && (
						<Button
							linkStyle
							onClick={() => {
								setNoUpgradesConfirmation(true);
								handleCustomizeOnClick();
							}}
							size='small'
							disabled={hasUpgrades}
							extraClasses={styles['disabled-button']}
							dataTags={{ 'data-no-lens-add-ons': true }}
						>
							{translations.noUpgrades}
						</Button>
					)}
				</>
			),
			type: BUILD_FLOW_STEPS.CUSTOMIZE,
			disabled: (isLensOpen || isPackageOpen) && !lensPackage && isNoUpgrades,
		},
	];

	return (
		<div className={styles.container}>
			{data.map(({ children, ...rest }, index) => (
				<QuickAddAccordion
					key={index}
					dataTags={dataTag}
					className={styles['accordian']}
					customRowStyle={styles['row-override']}
					containerClassName={styles['content']}
					{...rest}
				>
					{children}
				</QuickAddAccordion>
			))}
		</div>
	);
};

export default QuickAdd;
